import { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell, ReferenceLine } from "recharts";
import { TrendingUp, TrendingDown, Sparkles, Target, AlertTriangle } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_META = {
  sydney:    { flag: "🇦🇺", key: "session_sydney" },
  tokyo:     { flag: "🇯🇵", key: "session_tokyo" },
  london:    { flag: "🇬🇧", key: "session_london" },
  newyork:   { flag: "🇺🇸", key: "session_newyork" },
  overlap:   { flag: "⚡", key: "session_overlap" },
  off_hours: { flag: "🌙", key: "session_off_hours" },
};

export default function PersonalBestHours({ timeframe }) {
  const { t, dir } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/stats/best-hours`, { params: { timeframe } })
      .then(r => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [timeframe]);

  if (loading) return null;
  if (!data || !data.sessions?.length) {
    return (
      <section className="border-b border-zinc-900" data-testid="personal-best-hours-empty">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center gap-3 text-sm text-zinc-500">
          <Sparkles className="h-4 w-4 text-emerald-400" />
          <span>{t("pbh_no_data")}</span>
        </div>
      </section>
    );
  }

  const chartData = data.sessions.map(s => ({
    ...s,
    label: `${SESSION_META[s.session]?.flag || ""} ${t(SESSION_META[s.session]?.key || s.session)}`,
    fill: s.win_rate >= 60 ? "#10B981" : s.win_rate >= 45 ? "#F59E0B" : "#EF4444",
  }));

  const best = data.best;
  const worst = data.worst;

  return (
    <section className="border-b border-zinc-900" data-testid="personal-best-hours">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Target className="h-3.5 w-3.5 text-emerald-400" />
              {t("personal_best_title")}
            </h3>
            <p className="text-[11px] text-zinc-500">{t("personal_best_desc")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Chart */}
          <Card className="lg:col-span-2 bg-[#121212] border border-zinc-800 rounded-sm p-5">
            <div className="h-56" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                  <XAxis dataKey="label" stroke="#71717a" fontSize={11} tick={{ fontFamily: 'Cairo' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} stroke="#71717a" fontSize={10} orientation="right" axisLine={false} tickLine={false} tick={{ fontFamily: 'JetBrains Mono' }} tickFormatter={(v) => `${v}%`} />
                  <ReferenceLine y={50} stroke="#3f3f46" strokeDasharray="4 4" />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "1px solid #27272a", borderRadius: 2, fontFamily: "JetBrains Mono" }}
                    labelStyle={{ color: "#a1a1aa", fontFamily: "Cairo" }}
                    formatter={(_v, _n, item) => [`${item.payload.win_rate}% · ${item.payload.trades} trades`, ""]}
                  />
                  <Bar dataKey="win_rate" radius={[3, 3, 0, 0]}>
                    {chartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Best / Worst tips */}
          <div className="flex flex-col gap-3">
            {best && (
              <Card className="bg-emerald-500/5 border border-emerald-500/30 rounded-sm p-4" data-testid="pbh-best">
                <div className="flex items-center gap-2 text-emerald-400 text-[10px] uppercase tracking-wider font-bold mb-2">
                  <TrendingUp className="h-3 w-3" />
                  {t("best_session")}
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-2xl">{SESSION_META[best.session]?.flag}</span>
                  <span className="text-lg font-bold text-white">{t(SESSION_META[best.session]?.key || best.session)}</span>
                  <span className="num text-xl font-bold text-emerald-400 ms-auto">{best.win_rate}%</span>
                </div>
                <p className="text-[11px] text-emerald-200/80 leading-relaxed">
                  {t("pbh_tip_best").replace("{s}", t(SESSION_META[best.session]?.key || best.session))}
                </p>
                <div className="text-[10px] text-zinc-500 num mt-2">{best.trades} trades</div>
              </Card>
            )}
            {worst && worst.session !== best?.session && (
              <Card className="bg-red-500/5 border border-red-500/30 rounded-sm p-4" data-testid="pbh-worst">
                <div className="flex items-center gap-2 text-red-400 text-[10px] uppercase tracking-wider font-bold mb-2">
                  <AlertTriangle className="h-3 w-3" />
                  {t("worst_session")}
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-2xl">{SESSION_META[worst.session]?.flag}</span>
                  <span className="text-lg font-bold text-white">{t(SESSION_META[worst.session]?.key || worst.session)}</span>
                  <span className="num text-xl font-bold text-red-400 ms-auto">{worst.win_rate}%</span>
                </div>
                <p className="text-[11px] text-red-200/80 leading-relaxed">
                  {t("pbh_tip_worst").replace("{s}", t(SESSION_META[worst.session]?.key || worst.session))}
                </p>
                <div className="text-[10px] text-zinc-500 num mt-2">{worst.trades} trades</div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
